import { Helmet } from 'react-helmet-async';
import {
  KeyRound, Zap, Rocket, CalendarClock, GraduationCap, Headset,
  UserPlus, Puzzle, Gauge, Globe, CheckCircle2, RefreshCw, Kanban
} from 'lucide-react';
import { Eyebrow, Reveal, Section, SectionHeader, PageBanner, CTAStrip } from '../components/ui';

export default function ComoFunciona() {
  return (
    <main className="page-enter">
      <Helmet>
        <title>Como Funciona | MedAliados — Plataforma de Vendas para Corretoras</title>
        <meta name="description" content="Entenda como a MedAliados funciona: cadastro em 48h, leads qualificados, plataforma digital completa e suporte dedicado para escalar sua corretora de planos de saúde sênior." />
        <link rel="canonical" href="https://medaliados.com.br/como-funciona" />
        <meta property="og:title" content="Como Funciona | MedAliados — Plataforma de Vendas para Corretoras" />
        <meta property="og:description" content="Do cadastro à escala nacional em 3 etapas. Onboarding em 48h, pipeline com leads qualificados e gestão integrada da carteira." />
        <meta property="og:url" content="https://medaliados.com.br/como-funciona" />
      </Helmet>
      <PageBanner
        eyebrow="Como funciona"
        title="Uma plataforma completa para sua corretora vender mais, com menos esforço."
        sub="Do cadastro à escala nacional — um processo desenhado para previsibilidade e crescimento composto."
      />

      {/* MOTOR DE CONVERSÃO */}
      <Section bg="bege" className="py-24 md:py-32">
        <Reveal>
          <SectionHeader eyebrow="O motor de conversão" title="Três etapas para colocar sua corretora em operação"
            sub="Cada etapa é desenhada para entregar valor imediato à equipe comercial — sem fricção e sem ramp-up longo."
          />
        </Reveal>

        <div className="mt-16 grid md:grid-cols-3 gap-6 relative">
          <div className="hidden md:block absolute top-12 left-[8%] right-[8%] h-px bg-borda" />
          {[
            { n: "01", t: "Acesso", d: "Sua corretora se cadastra rapidamente na plataforma e recebe acesso imediato às ferramentas comerciais.", Icon: KeyRound },
            { n: "02", t: "Conversão", d: "Realize o atendimento de leads qualificados e feche vendas com suporte inteligente da plataforma.", Icon: Zap },
            { n: "03", t: "Tração", d: "Gestão contínua de vendas e acompanhamento de carteira diretamente pela plataforma.", Icon: Rocket },
          ].map((s, i) => (
            <Reveal key={i} delay={i * 120}>
              <div className="relative bg-fundo-branco rounded-2xl border border-borda p-7 h-full">
                <div className="relative flex items-center justify-between">
                  <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-verde-escuro text-fundo-branco font-display font-bold">{s.n}</span>
                  <span className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-verde-escuro/5 text-verde-medio">
                    <s.Icon size={20} />
                  </span>
                </div>
                <h3 className="font-display font-bold text-[1.4rem] text-verde-escuro mt-6">{s.t}</h3>
                <p className="mt-3 text-[0.98rem] text-texto-medio leading-relaxed">{s.d}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <div className="mt-14 grid md:grid-cols-3 gap-4">
          {[
            { label: "Onboarding em 48h", Icon: CalendarClock },
            { label: "Treinamento incluído", Icon: GraduationCap },
            { label: "Suporte dedicado", Icon: Headset },
          ].map((item, i) => (
            <Reveal key={i} delay={i * 80}>
              <div className="rounded-xl border border-borda bg-fundo-branco px-5 py-4 flex items-center gap-3">
                <item.Icon size={18} className="text-verde-medio" />
                <span className="text-verde-escuro font-medium">{item.label}</span>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* JORNADA */}
      <Section bg="branco" className="py-24 md:py-32">
        <Reveal>
          <SectionHeader eyebrow="A jornada da corretora"
            title="O caminho para a escala nacional"
            sub="Quatro marcos que estruturam o crescimento — do primeiro cadastro à liderança regional."
          />
        </Reveal>

        <div className="mt-16 relative">
          <div className="hidden md:block absolute top-[58px] left-[8%] right-[8%] h-px bg-borda" />
          <div className="grid md:grid-cols-4 gap-8 md:gap-4">
            {[
              { n: "1", t: "Cadastro", d: "Inserção rápida da corretora parceira no ecossistema MedAliados.", Icon: UserPlus },
              { n: "2", t: "Integração", d: "Onboarding comercial e domínio da plataforma digital pela equipe.", Icon: Puzzle },
              { n: "3", t: "Expansão", d: "Aceleração de vendas com previsibilidade e suporte técnico contínuo.", Icon: Gauge },
              { n: "4", t: "Escala Nacional", d: "Domínio do mercado sênior com portfólio de alta demanda.", Icon: Globe },
            ].map((m, i) => (
              <Reveal key={i} delay={i * 130}>
                <div className="flex flex-col items-center text-center">
                  <div className="relative">
                    <span className="inline-flex h-[116px] w-[116px] items-center justify-center rounded-full bg-fundo-bege border-2 border-verde-medio">
                      <m.Icon size={36} className="text-verde-escuro" />
                    </span>
                    <span className="absolute -top-2 -right-2 inline-flex h-9 w-9 items-center justify-center rounded-full bg-verde-escuro text-fundo-branco font-display font-bold border-4 border-fundo-branco">{m.n}</span>
                  </div>
                  <h3 className="font-display font-bold text-[1.25rem] text-verde-escuro mt-6">{m.t}</h3>
                  <p className="mt-2 text-[0.95rem] text-texto-medio leading-relaxed max-w-xs">{m.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </Section>

      {/* PLATAFORMA DETALHADA */}
      <Section bg="bege" className="py-24 md:py-32">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5 order-2 lg:order-1">
            <Reveal>
              <Eyebrow>O que sua equipe vê</Eyebrow>
              <h2 className="font-display font-bold h-section mt-4 text-verde-escuro">Tudo em um só lugar</h2>
              <p className="mt-5 text-[1.05rem] text-texto-medio leading-relaxed">
                Pipeline visual, qualificação automatizada, simulações de plano e gestão da carteira ativa. Sua equipe trabalha em uma única tela — sem janelas paralelas e sem retrabalho.
              </p>
              <ul className="mt-7 space-y-3">
                {["Pipeline com leads qualificados em tempo real", "Simulador e cotador integrados", "Gestão da carteira ativa", "Painel de comissionamento transparente", "Alertas inteligentes de oportunidade"].map((it, i) => (
                  <li key={i} className="flex items-start gap-3 text-texto-escuro">
                    <CheckCircle2 size={18} className="mt-0.5 text-verde-medio shrink-0" />
                    <span className="text-[0.98rem]">{it}</span>
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>

          <div className="lg:col-span-7 order-1 lg:order-2">
            <Reveal delay={100}>
              <div className="rounded-2xl bg-fundo-branco border border-borda shadow-card overflow-hidden">
                <div className="px-5 py-3 border-b border-borda flex items-center justify-between bg-fundo-bege">
                  <div className="flex items-center gap-2">
                    <span className="h-2 w-2 rounded-full bg-verde-medio" />
                    <span className="font-mono text-[11px] uppercase tracking-widest text-verde-escuro/70">Plataforma · módulo Pipeline</span>
                  </div>
                  <Kanban size={14} className="text-verde-medio" />
                </div>
                <div className="p-5 grid grid-cols-3 gap-3">
                  {[
                    { t: "Novos leads", c: 28, color: "#8BC34A" },
                    { t: "Em contato", c: 14, color: "#4CAF50" },
                    { t: "Fechamento", c: 7, color: "#2E7D32" },
                  ].map((col, ci) => (
                    <div key={ci} className="rounded-lg bg-fundo-bege border border-borda p-3">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-[12px] font-semibold text-verde-escuro">{col.t}</span>
                        <span className="text-[11px] font-mono px-2 py-0.5 rounded-full text-fundo-branco" style={{ background: col.color }}>{col.c}</span>
                      </div>
                      <div className="space-y-2">
                        {[0, 1, 2].map((k) => (
                          <div key={k} className="rounded-md bg-fundo-branco border border-borda p-2.5">
                            <div className="flex items-center justify-between">
                              <span className="font-mono text-[10px] text-verde-medio">CNPJ ••.•••/••••-{String(10 + ci * 3 + k).slice(-2)}</span>
                              <span className="h-1.5 w-1.5 rounded-full" style={{ background: col.color }} />
                            </div>
                            <div className="text-[11px] text-verde-escuro mt-1 font-medium">Corretora {String.fromCharCode(65 + ci * 3 + k)}{ci}{k}</div>
                            <div className="text-[10px] text-texto-medio mt-0.5">{["SP", "MG", "PR", "RJ", "BA"][(k + ci) % 5]} · Lead {ci === 0 ? "frio" : ci === 1 ? "morno" : "quente"}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="px-5 py-4 border-t border-borda bg-fundo-bege flex items-center justify-between">
                  <div className="font-mono text-[11px] text-texto-medio">[interface ilustrativa] Pipeline atualizado em tempo real</div>
                  <div className="flex items-center gap-2 text-verde-medio">
                    <RefreshCw size={14} />
                    <span className="text-[12px]">Sincronizado</span>
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </Section>

      <CTAStrip headline="Pronto para começar? Cadastre sua corretora agora." sub="Sua equipe pode estar operando em até 48h após o cadastro." label="Iniciar Cadastro" />
    </main>
  );
}
